import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import decodeHTMLEntities from "core-web/utils/decodeHTMLEntities";
import { getAvailabilitySchemaUrl, getProductAvailability } from "core-web/utils/getProductAvailability";
import { productBreadcrumbs as getProductBreadcrumbs } from "core-web/utils/storm";
import { toFixedCurrency } from "core-web/components/FormatPrice";
import { APP_ORGANIZATION } from "theme/config/constants";

const ProductStructuredData = ({
    brand,
    categories,
    currency,
    description,
    ean,
    images,
    mpn,
    name,
    price,
    sku,
    url,
    variants,
    productAvailability,
    includedProducts,
}) => {
    const appOrganization = APP_ORGANIZATION;
    const ecommerceEnabled = useSelector(({ application }) => application?.ecommerceEnabled);
    const productAvailabilityValue = getAvailabilitySchemaUrl(productAvailability);
    const { origin, protocol = "https://" } = useSelector(({ application }) => application);
    const { t } = useTranslation();

    const schema = {
        "@context": "http://schema.org",
        "@type": "Product",
        name,
        image: images,
        description,
        sku,
        productID: ean,
        gtin13: ean,
        mpn,
        brand: {
            "@type": "Brand",
            name: brand,
        },
    };

    if (ecommerceEnabled) {
        schema.offers = {
            "@type": "Offer",
            url,
            priceCurrency: currency,
            price: `${price}`,
            itemCondition: "http://schema.org/NewCondition",
            availability: productAvailabilityValue,
            seller: {
                "@type": "Organization",
                name: appOrganization,
            },
        };
    }

    if (includedProducts && includedProducts.length) {
        schema.includedProducts = includedProducts.map((product) => ({
            "@type": "Product",
            productID: product.id,
            name: product.name,
            image: product.image || images[0],
        }));
    }

    if (ecommerceEnabled && productAvailability.nextDelivery) {
        schema["offers"]["availabilityStarts"] = productAvailability.nextDelivery;
    }

    if (variants.length) {
        let lowestPriceProduct = variants.reduce((res, obj) => {
            return obj.price < res.price ? obj : res;
        });

        let highestPriceProduct = variants.reduce((res, obj) => {
            return obj.price > res.price ? obj : res;
        });

        lowestPriceProduct = lowestPriceProduct.price * lowestPriceProduct.vat_rate;
        highestPriceProduct = highestPriceProduct.price * highestPriceProduct.vat_rate;

        if (currency === "EUR") {
            lowestPriceProduct = toFixedCurrency(lowestPriceProduct, 2, ".");
            highestPriceProduct = toFixedCurrency(highestPriceProduct, 2, ".");
        } else {
            lowestPriceProduct =
                lowestPriceProduct % 1 !== 0 ? toFixedCurrency(lowestPriceProduct, 2, ".") : lowestPriceProduct;
            highestPriceProduct =
                highestPriceProduct % 1 !== 0 ? toFixedCurrency(highestPriceProduct, 2, ".") : highestPriceProduct;
        }

        // Only the aggregate price is ecommerce-gated; the variant list itself is catalogue data.
        if (ecommerceEnabled) {
            schema["offers"] = {
                "@type": "AggregateOffer",
                url,
                lowPrice: lowestPriceProduct,
                highPrice: highestPriceProduct,
                priceCurrency: currency,
                offerCount: variants.length,
            };
        }

        const productVariations = variants.map((variation) => {
            const variationAvailability = getProductAvailability(variation);
            const variationAvailabilityValue = getAvailabilitySchemaUrl(variationAvailability);

            const variationName = variation.variant_name || name;
            const variationImage =
                (variation.format || {}).image || (variation.files[0] && variation.files[0].format.image);
            const variationMpn = variation.manufacturer?.part_no || null;
            const variationSku = variation.part_no;
            const variationEan = variation.ean_code;
            let variationPrice = variation.price * variation.vat_rate;
            if (currency === "EUR") {
                variationPrice = toFixedCurrency(variationPrice, 2, ".");
            } else if (variationPrice % 1 !== 0) {
                variationPrice = toFixedCurrency(variationPrice, 2, ".");
            }

            const variationSchema = {
                "@context": "http://schema.org",
                "@type": "ProductModel",
                name: variationName,
                image: variationImage,
                description,
                sku: variationSku,
                productID: variationEan,
                gtin13: variationEan,
                mpn: variationMpn,
                brand: {
                    "@type": "Brand",
                    name: brand,
                },
                ...(ecommerceEnabled && {
                    offers: {
                        "@type": "Offer",
                        url: `${url}?p=${variation.part_no}`,
                        price: variationPrice,
                        priceCurrency: currency,
                        itemCondition: "http://schema.org/NewCondition",
                        availability: variationAvailabilityValue,
                        seller: {
                            "@type": "Organization",
                            name: appOrganization,
                        },
                    },
                }),
            };

            if (ecommerceEnabled && variationAvailability.nextDelivery) {
                variationSchema["offers"]["availabilityStarts"] = variationAvailability.nextDelivery;
            }

            return variationSchema;
        });

        schema["model"] = productVariations;
    }

    const breadcrumbs = [
        {
            "@type": "ListItem",
            position: 1,
            name: t("home"),
            item: protocol + origin,
        },
    ];

    const productBreadcrumbs = getProductBreadcrumbs(categories);

    if (productBreadcrumbs) {
        productBreadcrumbs.forEach((item, index) => {
            breadcrumbs.push({
                "@type": "ListItem",
                position: index + 2,
                name: decodeHTMLEntities(item.title),
                item: protocol + origin + item.permalink,
            });
        });
    }

    breadcrumbs.push({
        "@type": "ListItem",
        position: breadcrumbs.length + 1,
        name: decodeHTMLEntities(name),
    });

    const breadcrumbList = {
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs,
    };

    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(schema, null, 2)}</script>
            {!!breadcrumbs.length && (
                <script type="application/ld+json">{JSON.stringify(breadcrumbList, null, 0)}</script>
            )}
        </Helmet>
    );
};

ProductStructuredData.propTypes = {
    brand: PropTypes.string.isRequired,
    categories: PropTypes.array.isRequired,
    currency: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    ean: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
    mpn: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    productAvailability: PropTypes.shape({
        inStock: PropTypes.bool,
        preOrder: PropTypes.bool,
        nextDelivery: PropTypes.string,
    }).isRequired,
    sku: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    variants: PropTypes.array.isRequired,
    includedProducts: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            image: PropTypes.string,
        }),
    ).isRequired,
};

export default ProductStructuredData;
